export enum envs {
	LOCALHOST = "LOCALHOST",
	PREPRODUCTION = "PREPRODUCTION",
	PRODUCTION = "PRODUCTION",
}

const subdomain = 'https://pprd.swiftia.io'
// const subdomain = "https://b1976f40ffd0.ngrok-free.app";

export const configs = {
	appName: "Swiftia",
	env: (process.env.ENVIRONMENT as envs) ?? envs.LOCALHOST,
	YTShort: {
		n8n: {
			apiKey: process.env.N8N_API_KEY ?? "",
			shortsgenerationWorkflow: process.env.N8N_SHORTSGENERATION_WORKFLOW ?? "https://agents.mismai.li/webhook/pprdshortsgeneration-swiftia-mainflow",
		},
		failedDelay: Number.parseInt(process.env.YSHORT_FAILED_DELAY ?? "1200"), // 1200s = 20min  in seconds, delay for pending jobs to turn into Failed
		maxInputDuration: Number.parseInt(
			process.env.YSHORT_MAX_INPUT_DURATION ?? "7200",
		), // in seconds, default max duration is 2Hours
		minimumShortDuration: Number.parseFloat(
			process.env.AI_MINIMUM_SHORT_DURATION ?? "25",
		), // 25seconds
		maximumShortDuration: Number.parseFloat(
			process.env.AI_MAXIMUM_SHORT_DURATION ?? "90",
		), // 1min:30s
		minimumDefinedShortDuration: Number.parseFloat(
			process.env.DEFINED_MINIMUM_SHORT_DURATION ?? "10",
		), //  10seconds
		maximumDefinedShortDuration: Number.parseFloat(
			process.env.DEFINED_MAXIMUM_SHORT_DURATION ?? "120",
		), // 5 min
		minimumDefinedShortPrice: Number.parseFloat(
			process.env.DEFINED_SHORT_PRICE ?? "0.3",
		), // $0.1 min
		durationMinimumCost: Number.parseFloat(
			process.env.DURATION_MINIMUM_COST ?? "0.3",
		), // $0.30 minimum cost for auto-generated shorts
		youtubeDownloadPricePerGB: Number.parseFloat(
			process.env.YOUTUBE_DOWNLOAD_PRICE_PER_GB ?? "0.6",
		), // $0.6 per GB
		minimumYoutubeProcessingPrice: Number.parseFloat(
			process.env.YOUTUBE_PROCESSING_MINIMUM_PRICE ?? "0.1",
		), // $0.1 minimum cost for youtube processing
		youtubeDownloadMethod: (process.env.YOUTUBE_DOWNLOAD_METHOD ?? "n8n") as "n8n" | "sieve",
		renderPricePerMinute: Number.parseFloat(
			process.env.RENDER_PRICE_PER_MINUTE ?? "0.005",
		), // $0.5 per minute of rendered video
		minimumRenderPrice: Number.parseFloat(
			process.env.RENDER_MINIMUM_PRICE ?? "0.005",
		), // $0.2 minimum cost per render
	},
	webhooksTimeout: Number.parseInt(process.env.WEBHOOKS_TIMEOUT ?? "30000"), // in milliseconds (30 seconds)
	trialPeriodCredit: Number.parseInt(process.env.TRIAL_CREDIT ?? "2"),
	apiKeys: {
		prefix: process.env.APIKEY_PREFIX ?? "lcl",
	},
	socket: {
		ably: {
			clientApiKey: process.env.CLIENT_ABLY_APIKEY ?? "",
			apiKey: process.env.ABLY_APIKEY ?? "",
		},
	},
	mongo: {
		mongoUrl: process.env.MONGODB_URI ?? "mongodb://localhost:27017",
	},
	paypal: {
		mode: process.env.PAYPAL_MODE,
		clientId: process.env.PAYPAL_CLIENT_ID,
		secretId: process.env.PAYPAL_SECRET_ID,
	},
	huggingFace: {
		apiKey: (process.env.HUGGINFAACE_APIKEY as `hf_${string}`) ?? "",
	},
	AWS: {
		accessKey: process.env.AWS_ACCESS_KEY_ID,
		secretKey: process.env.AWS_SECRET_ACCESS_KEY,
		bucketName: process.env.BUCKETNAME,
		customDomain: process.env.S3_CUSTOM_DOMAIN ?? null,
		s3MediaTimeout: process.env.S3_UPLOAD_TIMEOUT ?? 1800000,
		s3MaxUploadSize: 1024 * 1024 * 100, // 100MB
		region: process.env.REGION ?? "us-east-1",
		mediaPath: process.env.S3_MEDIA_FOLDER_PATH ?? "media/",
		avatarsPath:
			process.env.S3_MEDIA_AVATAR_FOLDER_PATH ?? "media/avatars/",
		lambdas: {
			maxRetry: process.env.LAMBDA_MAX_RETRY
				? Number.parseInt(process.env.LAMBDA_MAX_RETRY)
				: 3,
			ffmpeg: process.env.LAMBDA_VIDEO_FFMPEG ?? "videoProcessor",
			shortGeneration:
				process.env.LAMBDA_SHORT_GENERATION ?? "shortGenerator",
			chatGPTAudio:
				process.env.LAMBDA_CHATGPT_AUDIO_GENERATION ??
				"requestChatGPTAudio",
			generateBRoll: process.env.LAMBDA_GENERATE_BROLL ?? "generateBRoll",
			remoteFileUpload:
				process.env.LAMBDA_REMOTE_FILE_UPLOAD ?? "remoteFileUpload",
			VideoShorts : {
				shortVideoTrim:
					process.env.LAMBDA_SHORT_VIDEO_TRIM ?? "VideoShorts-shortVideoTrim",
				youtubeVideoDownloader:
					process.env.LAMBDA_YOUTUBE_VIDEO_DOWNLOADER ?? "VideoShorts-youtubeVideoDownloader",
				getVideoASD:
					process.env.LAMBDA_GET_VIDEO_ASD ?? "VideoShorts-getVideoASD",
				videoShortFinish:
					process.env.LAMBDA_VIDEO_SHORT_FINISH ?? "VideoShorts-videoShortFinish",
			}
		},
	},
	google: {
		googleClientId: process.env.GOOGLE_CLIENT_ID || "",
		googleClientSecret: process.env.GOOGLE_CLIENT_SECRET || "",
		youtubeApiKey: process.env.YOUTUBE_APIKEY || "",
	},
	AI: {
		groc: {
			apiKey: process.env.GROQ_API_KEY,
			max_tokens: Number.parseInt(
				process.env.GROQ_API_MAX_TOKENS ?? "8192",
			),
		},
		gemini: {
			apiKey: process.env.GEMINI_API_KEY || "",
		},
		deepseek: {
			apiKey: process.env.DEEPSEEK_API_KEY || "",
			baseUrl:
				process.env.DEEPSEEK_BASE_URL?.trim() ||
				"https://api.deepseek.com",
		},
		chatgpt: {
			apiKey: process.env.CHATGPT_API_KEY || "",
		},
		maxRetry: Number.parseFloat(process.env.AI_GENERATE_COUNT ?? "3"), // seconds
		maxTimeRangeTranscriptionProcess: 600,
	},
	SIEV: {
		apiKeys: (process.env.SIEVE_API_KEY || "").split(","),
		ASD: {
			webhook:
				process.env.ASD_WEBHOOK ??
				`${subdomain}/api/webhooks/extern/asd`,
			webhookSecret:
				process.env.ASD_WEBHOOK_SECRET ?? "OIHQOHKSJQHQKJSNQKHSKQ",
		},
		youtubeDownloader: {
			webhook:
				process.env.YOUTUBEMP4_WEBHOOK ??
				`${subdomain}/api/webhooks/extern/ytdownload`,
			webhookSecret:
				process.env.YOUTUBEMP4_WEBHOOK_SECRET ??
				"OHNSIDGBSDAPOUDJNKSDHAOJKSND",
		},
		autoCrop: {
			webhook:
				process.env.AUTOCROP_WEBHOOK ??
				`${subdomain}/api/webhooks/extern/autocrop`,
			webhookSecret:
				process.env.AUTOCROP_WEBHOOK_SECRET ??
				"OHNSIDGBSDAPOUDJNKSDHAOJKSND",
		},
	},
	cerebrium: {
		apiKey: process.env.CEREBRIUM_API_KEY || "",
		autoCrop: {
			serviceUrl:
				process.env.CEREBRIUM_AUTOCROP_URL ??
				"https://api.cortex.cerebrium.ai/v4/p-d32531dc/tasd-23-support/run?async=true",
			webhook:
				process.env.AUTOCROP_WEBHOOK ??
				`${subdomain}/api/webhooks/extern/autocrop`,
			webhookSecret:
				process.env.AUTOCROP_WEBHOOK_SECRET ??
				"uyabjnpHIBKJNASKABKSkahisdbasd",
		},
	},
	gladia: {
		apiKey: process.env.GLADIA_API_KEY,
	},
	deepgram: {
		apiKey: process.env.DEEPGRAM_API_KEY,
	},
	modal: {
		trimServiceUrl: process.env.MODAL_TRIM_SERVICE_URL ?? "https://swiftia-io--swiftia-general-trim.modal.run",
		youtubeTrimUrl: process.env.MODAL_youtube_trim_webhook_url ?? "https://swiftia-io--swiftia-youtube-downloader-api-download.modal.run",
		asdServiceUrl: process.env.MODAL_ASD_SERVICE_URL ?? "https://swiftia-io--swiftia-general-api-process-video-asd.modal.run/",
		infinitetalkGenerateUrl:
			process.env.INFINITETALK_GENERATE_URL ??
			"https://mrfriendships48-pprd--swiftia-infinitetalk-api-generate.modal.run",
		infinitetalkWebhookSecret:
			process.env.INFINITETALK_WEBHOOK_SECRET ??
			process.env.SWIFTIA_WEBHOOK_SECRET ??
			"infinitetalk-webhook",
		wan22GenerateUrl:
			process.env.WAN22_GENERATE_URL ??
			"https://swiftia-pprd--swiftia-wan22-api-generate.modal.run",
		wan22ApiKey:
			process.env.WAN22_API_KEY ?? process.env.MODAL_WAN22_API_KEY ?? "",
		wan22WebhookSecret:
			process.env.WAN22_WEBHOOK_SECRET ??
			process.env.SWIFTIA_WEBHOOK_SECRET ??
			"wan22-webhook",
	},
	remotion: {
		webhookSecret: process.env.REMOTION_WEBHOOK_SECRET ?? "noSecret",
		maxConcurrentRenders: Number.parseInt(process.env.MAX_CONCURRENT_RENDERS ?? "1"),
	},
	public: {
		bucketName: process.env.BUCKETNAME || "",
	},
	pexels: {
		apiKey: process.env.PEXELS_API_KEY ?? "",
	},
	tavily: {
		apiKey: process.env.TAVILY_API_KEY?.trim() || "",
	},
	/**
	 * Jamendo client id for royalty-free music search
	 * (https://developer.jamendo.com/). Optional — presets + Wikimedia still work.
	 */
	jamendo: {
		clientId: process.env.JAMENDO_CLIENT_ID?.trim() || "",
	},
	/**
	 * Wikimedia Commons requires a descriptive User-Agent
	 * (https://foundation.wikimedia.org/wiki/Policy:Wikimedia_Foundation_User-Agent_Policy).
	 */
	wikimedia: {
		userAgent:
			process.env.WIKIMEDIA_USER_AGENT?.trim() ||
			"SwiftiaVideoProject/1.0 (https://swiftia.io; stock footage for documentaries)",
	},
	/**
	 * B-roll pipeline uses two different “merge” ideas — do not confuse them:
	 *
	 * 1) **Transcript segment merge** (`mergeTargetMinDurationSec`, `mergeMaxGapSec`, `mergeMaxWords`):
	 *    Runs early on `SentenceInput` rows from the transcription. Combines neighbouring speech
	 *    segments when the pause between them is small, so the AI and Pexels see fewer tiny
	 *    ~1.5s pages. Units: times are **seconds**, word limits are **word count**.
	 *
	 * 2) **Timeline clip snap** (`adjacentClipMergeMaxGapSec`):
	 *    Runs **after** each B-roll is a real media clip with `startTime` / `endTime` on the short.
	 *    Snaps adjacent clips so back-to-back cuts do not flicker. Unit: **seconds** on the same
	 *    timeline as `BRollItem.startTime` / `endTime` (not minutes).
	 *
	 * Other fields cap segment length, Pexels search hints, or default caption page size.
	 */
	bRoll: {
		/**
		 * Feature flags for B-roll asset classes.
		 * - If one type is disabled, generation falls back to the other type.
		 * - If both are disabled, B-roll generation returns zero items.
		 * Env:
		 * - `BROLL_ENABLE_IMAGE_ASSETS` (default: true)
		 * - `BROLL_ENABLE_VIDEO_ASSETS` (default: true)
		 */
		enableImageAssets: process.env.BROLL_ENABLE_IMAGE_ASSETS !== "false",
		enableVideoAssets: process.env.BROLL_ENABLE_VIDEO_ASSETS !== "false",
		/**
		 * Upper bound on how long one B-roll **speech segment** may be after splitting/merging,
		 * in **seconds** (same timebase as the video). Used when building sentences and clamping
		 * duration. Env: `BROLL_MAX_SEGMENT_DURATION_SEC`.
		 */
		maxSegmentDurationSec: Number.parseFloat(
			process.env.BROLL_MAX_SEGMENT_DURATION_SEC ?? "8",
		),
		/**
		 * When searching Pexels for **video** stock, minimum duration to ask for, in **seconds**
		 * (Pexels videos are often several seconds; avoids unusably short files). Not used for
		 * image B-roll. Env: `BROLL_MIN_PEXELS_VIDEO_DURATION_SEC`.
		 */
		minPexelsVideoSearchDurationSec: Number.parseFloat(
			process.env.BROLL_MIN_PEXELS_VIDEO_DURATION_SEC ?? "2",
		),
		/**
		 * Default max words per caption “page” when splitting transcription for B-roll sentence
		 * inputs (integer **word count**, not seconds). Env: `BROLL_DEFAULT_MAX_WORDS_PER_PAGE`.
		 */
		defaultMaxWordsPerPage: Number.parseInt(
			process.env.BROLL_DEFAULT_MAX_WORDS_PER_PAGE ?? "6",
			10,
		),
		/**
		 * Used only by `mergeAdjacentBrollSegments` on transcript segments: keep merging
		 * consecutive segments until the combined piece is at least this long, in **seconds**
		 * (subject to gap/word/max-duration limits). Env: `BROLL_MERGE_TARGET_MIN_DURATION_SEC`.
		 */
		mergeTargetMinDurationSec: Number.parseFloat(
			process.env.BROLL_MERGE_TARGET_MIN_DURATION_SEC ?? "2.75",
		),
		/**
		 * Used only by `mergeAdjacentBrollSegments`: max **silence** between two transcript
		 * segments (in **seconds**) to still treat them as one continuous moment and merge.
		 * This is **not** the same as `adjacentClipMergeMaxGapSec` (that one is for final
		 * timeline clips). Env: `BROLL_MERGE_MAX_GAP_SEC`.
		 */
		mergeMaxGapSec: Number.parseFloat(
			process.env.BROLL_MERGE_MAX_GAP_SEC ?? "0.5",
		),
		/**
		 * Used only by `mergeAdjacentBrollSegments`: merged transcript text must not exceed this
		 * many **words** (integer). Env: `BROLL_MERGE_MAX_WORDS`.
		 */
		mergeMaxWords: Number.parseInt(
			process.env.BROLL_MERGE_MAX_WORDS ?? "22",
			10,
		),
		/**
		 * Used only by `mergeAdjacentBRollClipTimings` on **generated** `BRollItem` clips:
		 * if the **gap** between clip A’s `endTime` and clip B’s `startTime` is **strictly less
		 * than this value**, both are adjusted to a single boundary (midpoint) so the edit does
		 * not strobe. Value is in **seconds** (e.g. `0.45` = 450 milliseconds), same unit as
		 * `BRollItem` times — **not** minutes. Overlapping clips (`B` starts before `A` ends) are
		 * always merged regardless of this value. Set to **0** to disable gap-only merging;
		 * overlaps are still fixed. Env: `BROLL_ADJACENT_CLIP_MERGE_MAX_GAP_SEC`.
		 */
		adjacentClipMergeMaxGapSec: Number.parseFloat(
			process.env.BROLL_ADJACENT_CLIP_MERGE_MAX_GAP_SEC ?? "1.5",
		),
		/**
		 * When true, tight positive gaps are hard-snapped so previous clip end equals next clip start
		 * (A.end = B.start). This removes tiny seam glitches better than midpoint snapping.
		 * Overlaps are still resolved by midpoint logic. Env: `BROLL_ADJACENT_CLIP_SNAP_TO_NEXT_START`.
		 */
		adjacentClipSnapToNextStart:
			process.env.BROLL_ADJACENT_CLIP_SNAP_TO_NEXT_START !== "false",
		/**
		 * Gap threshold (seconds) used by animation manager to suppress boundary in/out animations
		 * when two B-roll clips are very close (avoid double-motion seam glitches). Env:
		 * `BROLL_ANIMATION_BOUNDARY_GAP_SEC`.
		 */
		animationBoundaryGapSec: Number.parseFloat(
			process.env.BROLL_ANIMATION_BOUNDARY_GAP_SEC ?? "0.3",
		),
		/**
		 * If two consecutive generated B-roll clips point to the exact same source asset and
		 * their timeline gap is less than this threshold (seconds), they are collapsed into one
		 * longer clip to avoid duplicate back-to-back cuts.
		 * Env: `BROLL_CONSECUTIVE_SAME_ASSET_MERGE_MAX_GAP_SEC`.
		 */
		consecutiveSameAssetMergeMaxGapSec: Number.parseFloat(
			process.env.BROLL_CONSECUTIVE_SAME_ASSET_MERGE_MAX_GAP_SEC ?? "2",
		),
		/**
		 * Post-LLM short-form B-roll rules (TikTok/Reels style): total coverage band, clip length,
		 * scaled clip counts, hook-window ordering. Set `BROLL_SHORT_FORM_ENABLED=false` to disable
		 * and keep legacy “all expressive segments” behavior.
		 */
		shortForm: {
			enabled: process.env.BROLL_SHORT_FORM_ENABLED !== "false",
			minCoverageRatio: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_MIN_COVERAGE_RATIO ?? "0.25",
			),
			maxCoverageRatio: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_MAX_COVERAGE_RATIO ?? "0.40",
			),
			minClipSec: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_MIN_CLIP_SEC ?? "2",
			),
			maxClipSec: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_MAX_CLIP_SEC ?? "4",
			),
			hookWindowSec: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_HOOK_WINDOW_SEC ?? "3",
			),
			referenceDurationSec: Number.parseFloat(
				process.env.BROLL_SHORT_FORM_REFERENCE_DURATION_SEC ?? "60",
			),
			referenceMinClips: Number.parseInt(
				process.env.BROLL_SHORT_FORM_REFERENCE_MIN_CLIPS ?? "4",
				10,
			),
			referenceMaxClips: Number.parseInt(
				process.env.BROLL_SHORT_FORM_REFERENCE_MAX_CLIPS ?? "8",
				10,
			),
			hookPreferThresholdPercent: Number.parseInt(
				process.env.BROLL_SHORT_FORM_HOOK_PREFER_THRESHOLD_PERCENT ?? "50",
				10,
			),
		},
	},
	videoDownloadApi: {
		apiKey: process.env.VIDEO_DOWNLOAD_API_KEY ?? "",
		primaryEndpoint: process.env.VIDEO_DOWNLOAD_API_ENDPOINT ?? "https://p.savenow.to",
		secondaryEndpoint: process.env.VIDEO_DOWNLOAD_API_ENDPOINT_SECONDARY ?? "https://p.lbserver.xyz",
	},
	jobs: {
		defaultAutoArchiveDurationDays: Number.parseInt(
			process.env.DEFAULT_AUTO_ARCHIVE_DURATION_DAYS ?? "90",
		), // Default number of days before jobs are automatically archived
		maxAutoArchiveDurationDays: 180, // Maximum allowed auto-archive duration in days
	},
	zernio: {
		apiKey: process.env.ZERNIO_API_KEY ?? "",
		baseUrl: process.env.ZERNIO_API_BASE_URL ?? "https://zernio.com/api/v1",
		webhookSecret: process.env.ZERNIO_WEBHOOK_SECRET ?? "mysecret",
		/** Comma-separated platform keys: when posting to any of these, pass-through media URLs are copied to managed storage first. `SOCIAL_REHOST_MEDIA_PLATFORMS` overrides the legacy `ZERNIO_REHOST_MEDIA_PLATFORMS`. Default: `facebook`. */
		platformsRequiringMediaRehost: (process.env.SOCIAL_REHOST_MEDIA_PLATFORMS ??
			process.env.ZERNIO_REHOST_MEDIA_PLATFORMS ??
			"facebook")
			.split(/[\s,;]+/)
			.map((value) => value.trim().toLowerCase())
			.filter((value) => value.length > 0),
	},
};
